import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import { Button, Input, Space, Image, Typography } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';
import cn from 'classnames';
import { CalendarOutlined, LinkOutlined } from '@ant-design/icons';

import UserShortCard from '@components/UserShortCard';
import { FULL_TIME_FORMAT } from '@config/format';
import IComment from '@interfaces/IComment';
import { formatPostUrl, getPath, goTo } from '@helpers/navigation';
import { IImage } from '@interfaces/IImage';
import useModal from '@hooks/useModal';
import ImageModal from '@components/FullImageModal/FullImageModal';
import { FileType } from '@interfaces/MediaFile';
import useTranslate from '@hooks/useTranslate';
import auth from '@stores/auth';

import { checkComment, replyToComment } from '@stores/mentionsOfficialAccountComment';
import { block as blockComment } from '@stores/comments';

import componentContent from './MentionsOfficialAccountCommentCard.module.json';
import css from './MentionsOfficialAccountCommentCard.module.less';

interface MentionsOfficialAccountCommentCardProps {
  comment: IComment;
}

const { TextArea } = Input;

const MentionsOfficialAccountCommentCard: FunctionComponent<
  MentionsOfficialAccountCommentCardProps
> = ({ comment }) => {
  const [currentImage, setCurrentImage] = useState<IImage | null>(null);
  const { user: authUser } = useStore(auth);
  const replyAccountId = authUser?.id ? String(authUser.id) : '';

  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');

  const [isChecking, setIsChecking] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isSpamming, setIsSpamming] = useState(false);

  const skipDisabled = isReplying || isChecking || isSpamming;
  const actionsDisabled = isReplyOpen || isChecking || isReplying || isSpamming;
  const sendDisabled =
    !replyText.trim() || !replyAccountId || isReplying || isChecking || isSpamming;

  const [isVisible, openModal, closeModal] = useModal();

  const content = useTranslate(componentContent);
  const postUrl = comment.mobileUrlPost ? formatPostUrl(comment.mobileUrlPost) : null;

  const goToUserDetail = () => goTo('userDetail', { userId: comment.author.id });

  const getCommentGifUrl = (comment: IComment): string | null => {
    if (typeof comment.gifUrl === 'string' && comment.gifUrl) return comment.gifUrl;

    const contentGif = Array.isArray(comment.content)
      ? comment.content.find((c) => typeof c?.gifUrl === 'string' && c.gifUrl)?.gifUrl
      : undefined;

    if (contentGif) return contentGif;

    return null;
  };

  const renderCommentText = (comment: IComment) => {
    const gifUrl = getCommentGifUrl(comment);

    if (gifUrl) {
      return (
        <>
          <a href={gifUrl}>{gifUrl}</a>
          <br />
          <Image
            src={gifUrl}
            width={120}
          />
        </>
      );
    }

    return comment.text;
  };

  const openModalHandler = useCallback(
    (image: IImage) => {
      setCurrentImage(image);
      openModal();
    },
    [openModal],
  );

  const onSkip = useCallback(() => {
    if (actionsDisabled) return;
    setIsChecking(true);
    checkComment({
      commentId: comment.id,
      preloaderId: `checkComment_${comment.id}`,
    }).finally(() => setIsChecking(false));
  }, [actionsDisabled, comment.id]);

  const onOpenReply = () => setIsReplyOpen(true);

  const onCancelReply = () => {
    setIsReplyOpen(false);
    setReplyText('');
  };

  const onSendReply = () => {
    const text = replyText.trim();
    if (!text || !replyAccountId || isReplying || isChecking) return;

    setIsReplying(true);

    replyToComment({
      commentId: String(comment.id),
      text,
      replyAccountId,
      images: [],
    }).finally(() => setIsReplying(false));
  };

  const onSpam = () => {
    if (actionsDisabled) return;
    setIsSpamming(true);
    blockComment({
      commentId: comment.id,
      preloaderId: `block_${comment.id}`,
    }).finally(() => setIsSpamming(false));
  };

  useEffect(() => {
    const unsub = replyToComment.done.watch(({ params }) => {
      if (String(params.commentId) === String(comment.id)) {
        onCancelReply();
      }
    });

    return () => unsub();
  }, [comment.id]);

  return (
    <>
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.header}>
            <div className={css.headerUserName}>
              <UserShortCard
                user={comment.author}
                onClick={goToUserDetail}
              />
              <div className={css.meta}>
                <span className={css.metaIcon}>
                  <CalendarOutlined />
                </span>
                <span>{moment(comment.whenCommented).format(FULL_TIME_FORMAT)}</span>
              </div>
            </div>
            <div className={cn(css.metaLine, css.meta)}>
              {postUrl ? (
                <Typography.Link
                  copyable={{ text: postUrl }}
                  href={postUrl}
                  target='_blank'
                  className={cn(css.metaLineItem, 'clickable')}
                >
                  <span className={css.metaIcon}>
                    <LinkOutlined />
                  </span>
                  <span>{content.originalPost}</span>
                </Typography.Link>
              ) : (
                <Link
                  className={cn(css.metaLineItem, 'clickable')}
                  to={getPath('postDetail', { postId: comment.postId })}
                >
                  <span className={css.metaIcon}>
                    <LinkOutlined />
                  </span>
                  <span>{content.originalPost}</span>
                </Link>
              )}
            </div>
          </div>

          <div className={css.body}>
            {comment.image && (
              <div>
                <img
                  onClick={() => comment.image && openModalHandler(comment.image)}
                  className={css.image}
                  src={comment?.image?.url}
                  alt=''
                />
              </div>
            )}
            <div>
              <span>{renderCommentText(comment)}</span>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <Space>
              <Button
                onClick={onOpenReply}
                disabled={isChecking || isReplying || actionsDisabled}
                loading={isReplying}
              >
                {content.reply}
              </Button>

              <Button
                onClick={onSkip}
                disabled={skipDisabled || actionsDisabled}
                loading={isChecking}
              >
                {content.skip}
              </Button>

              <Button
                onClick={onSpam}
                disabled={actionsDisabled}
                loading={isSpamming}
              >
                {content.spam}
              </Button>
            </Space>

            {isReplyOpen ? (
              <div style={{ marginTop: 12 }}>
                <TextArea
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={content.replyPlaceholder}
                />

                <div style={{ marginTop: 12 }}>
                  <Space>
                    <Button
                      onClick={onCancelReply}
                      disabled={isReplying}
                    >
                      {content.cancel}
                    </Button>

                    <Button
                      type='primary'
                      onClick={onSendReply}
                      disabled={sendDisabled}
                      loading={isReplying}
                    >
                      {content.send}
                    </Button>
                  </Space>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isVisible ? (
        <ImageModal
          onClose={closeModal}
          files={[
            {
              fileType: FileType.Image,
              image: currentImage,
            },
          ]}
        />
      ) : null}
    </>
  );
};

export default MentionsOfficialAccountCommentCard;
